import { useState, useEffect } from 'react';
import { Search, Crown, Building2, ChevronLeft, ChevronRight, AlertTriangle, Check } from 'lucide-react';

interface Provider {
  id: string;
  email: string;
  name: string | null;
  name_en: string | null;
  phone: string | null;
  company_name: string | null;
  ceo_name: string | null;
  ceo_name_en: string | null;
  is_premium: boolean;
  premium_started_at: string | null;
  premium_expires_at: string | null;
  created_at: string;
  avatar_url: string | null;
  tagline: string | null;
}

export default function AdminPremiumTab() {
  const [providers, setProviders] = useState<Provider[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeSearch, setActiveSearch] = useState('');
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [updatingId, setUpdatingId] = useState<string | null>(null);
  const [confirmModal, setConfirmModal] = useState<{ provider: Provider; newStatus: boolean } | null>(null);

  // 기업 한줄 소개 편집 상태 (행별 입력값 / 저장 중 ID)
  const [taglineDrafts, setTaglineDrafts] = useState<Record<string, string>>({});
  const [savingTaglineId, setSavingTaglineId] = useState<string | null>(null);

  // 페이지네이션
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const pageSize = 10;

  // 교육업체 목록 조회
  const fetchProviders = async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: pageSize.toString(),
        ...(activeSearch && { search: activeSearch }),
      });

      console.log('[AdminPremiumTab] Fetching providers...');
      const response = await fetch(`/api/v1/admin/providers?${params}`, {
        credentials: 'include',
      });

      console.log('[AdminPremiumTab] Response status:', response.status);

      if (!response.ok) {
        throw new Error('교육업체 목록을 불러오는데 실패했습니다.');
      }

      const data = await response.json();
      console.log('[AdminPremiumTab] Data:', data);
      setProviders(data.users);
      setTotalPages(data.totalPages);
      setTotalCount(data.totalCount);
      // 기업 한줄 소개 드래프트 초기화
      const drafts: Record<string, string> = {};
      (data.users as Provider[]).forEach((p) => {
        drafts[p.id] = p.tagline || '';
      });
      setTaglineDrafts(drafts);
    } catch (err) {
      console.error('[AdminPremiumTab] Error:', err);
      setError(err instanceof Error ? err.message : '오류가 발생했습니다.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProviders();
  }, [page, activeSearch]);

  // 검색
  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setPage(1);
    setActiveSearch(searchTerm);
  };

  // 전체보기
  const handleShowAll = () => {
    setSearchTerm('');
    setActiveSearch('');
    setPage(1);
  };

  // 인증 우수 상태 토글 - 확인 모달 표시
  const handleTogglePremium = async (provider: Provider) => {
    const newStatus = !provider.is_premium;
    setConfirmModal({ provider, newStatus });
  };

  // 인증 우수 상태 변경 확인
  const confirmTogglePremium = async () => {
    if (!confirmModal) return;

    const { provider, newStatus } = confirmModal;
    setUpdatingId(provider.id);
    setMessage(null);
    setConfirmModal(null);

    try {
      const response = await fetch(`/api/v1/admin/providers/${provider.id}/premium`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ is_premium: newStatus }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || '인증 우수 상태 변경에 실패했습니다.');
      }

      setMessage({ type: 'success', text: data.message });

      // 목록 새로고침
      await fetchProviders();
    } catch (err) {
      setMessage({
        type: 'error',
        text: err instanceof Error ? err.message : '오류가 발생했습니다.',
      });
    } finally {
      setUpdatingId(null);
    }
  };

  // 기업 한줄 소개 변경 여부
  const isTaglineDirty = (provider: Provider) => {
    const draft = taglineDrafts[provider.id];
    if (draft === undefined) return false;
    return draft !== (provider.tagline || '');
  };

  // 기업 한줄 소개 저장
  const handleSaveTagline = async (provider: Provider) => {
    const tagline = taglineDrafts[provider.id] ?? '';
    setSavingTaglineId(provider.id);
    setMessage(null);
    try {
      const response = await fetch(`/api/v1/admin/providers/${provider.id}/tagline`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ tagline }),
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || '기업 한줄 소개 저장에 실패했습니다.');
      }
      setMessage({
        type: 'success',
        text: `${provider.company_name || '업체'} — 기업 한줄 소개가 저장되었습니다.`,
      });
      await fetchProviders();
    } catch (err) {
      setMessage({
        type: 'error',
        text: err instanceof Error ? err.message : '오류가 발생했습니다.',
      });
    } finally {
      setSavingTaglineId(null);
    }
  };

  // 날짜 포맷
  const formatDate = (dateString: string | null) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('ko-KR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  // 우수 인증 교육 기관 수 계산
  const premiumCount = providers.filter(p => p.is_premium).length;

  return (
    <div>
      {/* 헤더 */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <Crown className="w-6 h-6 text-amber-500" />
          우수 인증 교육 기관 관리
        </h1>
        <p className="text-gray-500 mt-1">
          전체 교육업체 {totalCount}개 / 우수 인증 교육 기관 {premiumCount}개
        </p>
      </div>

      {/* 메시지 */}
      {message && (
        <div
          className={`mb-4 p-4 rounded-lg ${
            message.type === 'success' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'
          }`}
        >
          {message.text}
        </div>
      )}

      {/* 검색 */}
      <form onSubmit={handleSearch} className="mb-6">
        <div className="flex gap-2">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="업체명, 대표자명, 이메일로 검색..."
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary"
            />
          </div>
          <button
            type="submit"
            className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-600 transition-colors"
          >
            검색
          </button>
          {activeSearch && (
            <button
              type="button"
              onClick={handleShowAll}
              className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
            >
              전체보기
            </button>
          )}
        </div>
        {activeSearch && (
          <p className="mt-2 text-sm text-gray-500">
            "<span className="font-medium text-gray-700">{activeSearch}</span>" 검색 결과: {totalCount}개
          </p>
        )}
      </form>

      {/* 테이블 */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-gray-500">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
            로딩 중...
          </div>
        ) : error ? (
          <div className="p-8 text-center text-red-500">{error}</div>
        ) : providers.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            <Building2 className="w-12 h-12 mx-auto mb-4 text-gray-300" />
            등록된 교육업체가 없습니다.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[1200px]">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">업체</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">대표자</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">이메일</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">연락처</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">가입일</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase whitespace-nowrap">인증 우수 상태</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">인증 우수 시작일</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">기업 한줄 소개</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {providers.map(provider => (
                  <tr key={provider.id} className={`hover:bg-gray-50 ${provider.is_premium ? 'bg-amber-50/30' : ''}`}>
                    {/* 업체 */}
                    <td className="px-4 py-3 whitespace-nowrap">
                      <div className="flex items-center gap-3">
                        {provider.avatar_url ? (
                          <img
                            src={provider.avatar_url}
                            alt=""
                            className="w-10 h-10 rounded-lg object-cover"
                          />
                        ) : (
                          <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center text-primary">
                            <Building2 className="w-5 h-5" />
                          </div>
                        )}
                        <div>
                          <div className="font-medium text-gray-900 flex items-center gap-2">
                            {provider.company_name || '업체명 없음'}
                            {provider.is_premium && (
                              <Crown className="w-4 h-4 text-amber-500" />
                            )}
                          </div>
                        </div>
                      </div>
                    </td>

                    {/* 대표자 */}
                    <td className="px-4 py-3 whitespace-nowrap">
                      <div>
                        <div className="text-gray-900">{provider.ceo_name || provider.name || '-'}</div>
                        <div className="text-gray-500 text-xs">{provider.ceo_name_en || provider.name_en || ''}</div>
                      </div>
                    </td>

                    {/* 이메일 */}
                    <td className="px-4 py-3 whitespace-nowrap">
                      <span className="text-gray-600 text-sm">{provider.email}</span>
                    </td>

                    {/* 연락처 */}
                    <td className="px-4 py-3 whitespace-nowrap">
                      <span className="text-gray-600 text-sm">{provider.phone || '-'}</span>
                    </td>

                    {/* 가입일 */}
                    <td className="px-4 py-3 whitespace-nowrap">
                      <span className="text-gray-500 text-sm">{formatDate(provider.created_at)}</span>
                    </td>

                    {/* 인증 우수 상태 */}
                    <td className="px-4 py-3 whitespace-nowrap text-center">
                      <button
                        onClick={() => handleTogglePremium(provider)}
                        disabled={updatingId === provider.id}
                        className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 ${
                          provider.is_premium ? 'bg-amber-500' : 'bg-gray-200'
                        } ${updatingId === provider.id ? 'opacity-50 cursor-not-allowed' : ''}`}
                      >
                        <span
                          className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                            provider.is_premium ? 'translate-x-5' : 'translate-x-0'
                          }`}
                        />
                      </button>
                    </td>

                    {/* 인증 우수 시작일 */}
                    <td className="px-4 py-3 whitespace-nowrap">
                      <span className="text-gray-500 text-sm">
                        {provider.is_premium ? formatDate(provider.premium_started_at) : '-'}
                      </span>
                    </td>

                    {/* 기업 한줄 소개 */}
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <input
                          type="text"
                          maxLength={100}
                          value={taglineDrafts[provider.id] ?? ''}
                          onChange={(e) =>
                            setTaglineDrafts((prev) => ({ ...prev, [provider.id]: e.target.value }))
                          }
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' && isTaglineDirty(provider)) handleSaveTagline(provider);
                          }}
                          placeholder="예) 20년 전통의 필라테스 전문 교육기관"
                          className={`w-64 px-3 py-1.5 border rounded-lg text-sm focus:ring-2 focus:ring-primary focus:border-transparent ${
                            isTaglineDirty(provider) ? 'border-accent bg-orange-50' : 'border-gray-300'
                          }`}
                        />
                        <button
                          onClick={() => handleSaveTagline(provider)}
                          disabled={!isTaglineDirty(provider) || savingTaglineId === provider.id}
                          className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-sm font-medium text-white bg-primary hover:bg-primary/90 disabled:opacity-40 disabled:cursor-not-allowed whitespace-nowrap"
                        >
                          <Check className="w-3.5 h-3.5" />
                          {savingTaglineId === provider.id ? '저장 중' : '저장'}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* 페이지네이션 */}
        {totalPages > 1 && (
          <div className="px-4 py-3 border-t border-gray-200 flex items-center justify-between">
            <p className="text-sm text-gray-500">
              총 {totalCount}개 중 {(page - 1) * pageSize + 1}-{Math.min(page * pageSize, totalCount)}개
            </p>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
                className="p-1 rounded hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              <span className="text-sm text-gray-700">
                {page} / {totalPages}
              </span>
              <button
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                className="p-1 rounded hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* 안내 메시지 */}
      <div className="mt-4 p-4 bg-amber-50 rounded-lg border border-amber-200">
        <h3 className="font-medium text-amber-800 flex items-center gap-2">
          <Crown className="w-4 h-4" />
          우수 인증 교육 기관 안내
        </h3>
        <ul className="mt-2 text-sm text-amber-700 space-y-1">
          <li>• 우수 인증 교육 기관는 메인 배너에 노출됩니다.</li>
          <li>• 우수 인증 교육 기관는 업체 상세 페이지를 편집할 수 있습니다.</li>
          <li>• 인증 우수 상태는 언제든지 변경할 수 있습니다.</li>
        </ul>
      </div>

      {/* 확인 모달 */}
      {confirmModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full mx-4 p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                confirmModal.newStatus ? 'bg-amber-100' : 'bg-gray-100'
              }`}>
                {confirmModal.newStatus ? (
                  <Crown className="w-6 h-6 text-amber-500" />
                ) : (
                  <AlertTriangle className="w-6 h-6 text-gray-500" />
                )}
              </div>
              <h2 className="text-xl font-bold text-gray-900">
                {confirmModal.newStatus ? '인증 우수 활성화' : '인증 우수 비활성화'}
              </h2>
            </div>

            <div className="mb-6">
              <p className="text-gray-700 mb-3">
                <span className="font-semibold">{confirmModal.provider.company_name || '업체명 없음'}</span>의
                인증 우수 상태를 {confirmModal.newStatus ? '활성화' : '비활성화'}하시겠습니까?
              </p>

              {confirmModal.newStatus ? (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                  <p className="text-sm text-amber-800">
                    <strong>활성화 시:</strong>
                  </p>
                  <ul className="text-sm text-amber-700 mt-1 space-y-1">
                    <li>• 메인 배너에 노출됩니다</li>
                    <li>• 업체 정보 편집이 가능합니다</li>
                    <li>• 해당 업체에 알림이 발송됩니다</li>
                  </ul>
                </div>
              ) : (
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                  <p className="text-sm text-gray-800">
                    <strong>비활성화 시:</strong>
                  </p>
                  <ul className="text-sm text-gray-700 mt-1 space-y-1">
                    <li>• 메인 배너에서 제거됩니다</li>
                    <li>• 업체 정보 편집이 제한됩니다</li>
                    <li>• 해당 업체에 알림이 발송됩니다</li>
                  </ul>
                </div>
              )}
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => setConfirmModal(null)}
                className="flex-1 px-4 py-2.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium"
              >
                취소
              </button>
              <button
                onClick={confirmTogglePremium}
                className={`flex-1 px-4 py-2.5 text-white rounded-lg transition-colors font-medium ${
                  confirmModal.newStatus
                    ? 'bg-amber-500 hover:bg-amber-600'
                    : 'bg-gray-600 hover:bg-gray-700'
                }`}
              >
                {confirmModal.newStatus ? '활성화' : '비활성화'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
